/**
 * Participants editor — the "Same as…" merge rules. Diarization sometimes
 * splits one person into two ids; the admin marks a row as the same person as
 * another row (standalone desktop calls only — see MERGEABLE_SESSION_SOURCE).
 * Pure and React-free: both desktop apps implement the same rules, and the
 * server enforces them again.
 *
 * Rules (no chains, owner and own-mic reps never a source):
 * - a row offers "Same as…" only when it is not the call owner's own row, is
 *   not a rep who joined with their own mic (`hasOwnMic`), is not already
 *   merged, is not itself a merge target, and has at least two other rows on
 *   the sheet;
 * - its targets are the other rows that are not merged;
 * - a merge target shows its own turns plus every row merged into it.
 */
import { isMerged, MergeMap, ParticipantRow } from './participantRows';

/**
 * The creator's own participant id on a standalone (desktop) session. On a bot
 * call the portal cannot tell which recall participant is the creator, so there
 * is no owner row and the server's validation is the backstop.
 */
export const STANDALONE_OWNER_PARTICIPANT_ID = 'salesperson';

/** The call owner's row id when the sheet can tell (standalone), else null. */
export const resolveOwnerParticipantId = (rows: ParticipantRow[]): string | null =>
  rows.some((row) => row.participantId === STANDALONE_OWNER_PARTICIPANT_ID) ? STANDALONE_OWNER_PARTICIPANT_ID : null;

/** A split voice needs the other side of the call plus its twin: fewer other rows means no menu. */
const MIN_OTHER_ROWS_FOR_MERGE = 2;

export const isMergeTarget = (merges: MergeMap, participantId: string): boolean =>
  Object.values(merges).includes(participantId);

/**
 * The rows `participantId` could be marked the same as: every other row not
 * merged away, in row order. The owner's row is a valid target.
 */
export const mergeTargets = (rows: ParticipantRow[], merges: MergeMap, participantId: string): ParticipantRow[] =>
  rows.filter((row) => row.participantId !== participantId && !isMerged(merges, row.participantId));

/** Whether a row shows the "Same as…" menu. */
export const canOfferMerge = (
  rows: ParticipantRow[],
  merges: MergeMap,
  participantId: string,
  ownerParticipantId: string | null = null,
): boolean =>
  participantId !== ownerParticipantId &&
  !rows.some((row) => row.participantId === participantId && row.hasOwnMic) &&
  !isMerged(merges, participantId) &&
  !isMergeTarget(merges, participantId) &&
  rows.filter((row) => row.participantId !== participantId).length >= MIN_OTHER_ROWS_FOR_MERGE &&
  mergeTargets(rows, merges, participantId).length > 0;

/**
 * Mark `sourceId` as the same person as `targetId`. An invalid choice (self,
 * unknown row, owner as source, chain in either direction) leaves the merges
 * unchanged, so the UI can never build a request the server would refuse.
 */
export const mergeRow = (
  rows: ParticipantRow[],
  merges: MergeMap,
  sourceId: string,
  targetId: string,
  ownerParticipantId: string | null = null,
): MergeMap => {
  if (!canOfferMerge(rows, merges, sourceId, ownerParticipantId)) return merges;
  const isValidTarget = mergeTargets(rows, merges, sourceId).some((row) => row.participantId === targetId);
  return isValidTarget ? { ...merges, [sourceId]: targetId } : merges;
};

/** Undo one row's merge; the row returns with its own name and role. */
export const undoMerge = (merges: MergeMap, sourceId: string): MergeMap => {
  if (!isMerged(merges, sourceId)) return merges;
  const { [sourceId]: _undone, ...remaining } = merges;
  return remaining;
};

/** A row's turns plus the turns of every row merged into it. */
export const combinedTurnCount = (rows: ParticipantRow[], merges: MergeMap, participantId: string): number =>
  rows
    .filter((row) => row.participantId === participantId || merges[row.participantId] === participantId)
    .reduce((total, row) => total + (row.turnCount || 0), 0);
